// #706 — id-valued canonical-brand combobox for the CRM lead form.
//
// Closed vocabulary (no free-text create row — manufacturer_id is a validated
// FK into canonical_manufacturers) and nullable: re-selecting the current
// brand clears it, the CustomerSearchCombobox toggle-off idiom. Fetches once
// via useCanonicalManufacturers and filters client-side (deterministic and
// testable — the BrandCombobox pattern, shouldFilter=false).

import * as React from 'react';
import { cn } from '@/lib/utils';
import { Button, ButtonArrow } from '@marine/ui/button';
import {
  Command,
  CommandCheck,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@marine/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@marine/ui/popover';
import { ScrollArea } from '@marine/ui/scroll-area';
import { useCanonicalManufacturers } from '@/hooks/useCanonicalManufacturers';

export interface ManufacturerComboboxProps {
  value: string | null | undefined;
  onChange: (id: string | null) => void;
  id?: string;
  disabled?: boolean;
  placeholder?: string;
}

export function ManufacturerCombobox({
  value,
  onChange,
  id,
  disabled = false,
  placeholder = 'Any brand',
}: ManufacturerComboboxProps) {
  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState('');
  const { data, isLoading } = useCanonicalManufacturers('boat');
  const options = data ?? [];

  const trimmed = search.trim().toLowerCase();
  const filtered =
    trimmed === '' ? options : options.filter((o) => o.name.toLowerCase().includes(trimmed));
  const selected = options.find((o) => o.id === value);

  const commit = (nextId: string) => {
    onChange(nextId === value ? null : nextId);
    setOpen(false);
    setSearch('');
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          id={id}
          type="button"
          variant="outline"
          role="combobox"
          mode="input"
          placeholder={!selected}
          aria-expanded={open}
          disabled={disabled}
          className="w-full justify-between"
        >
          {selected ? (
            <span className="truncate">{selected.name}</span>
          ) : (
            <span className="text-muted-foreground">{isLoading ? 'Loading…' : placeholder}</span>
          )}
          <ButtonArrow />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[--radix-popover-trigger-width]" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Search brands…"
            value={search}
            onValueChange={setSearch}
          />
          <CommandList>
            <ScrollArea viewportClassName="max-h-[260px] [&>div]:block!">
              <CommandEmpty>{isLoading ? 'Loading…' : 'No brands found.'}</CommandEmpty>
              <CommandGroup>
                {filtered.map((opt) => (
                  <CommandItem key={opt.id} value={opt.id} onSelect={() => commit(opt.id)}>
                    <span className="truncate">{opt.name}</span>
                    <CommandCheck
                      className={cn(value === opt.id ? 'opacity-100' : 'opacity-0')}
                    />
                  </CommandItem>
                ))}
              </CommandGroup>
            </ScrollArea>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
